import { formatOptionalNumber, isFiniteNumber, normalizeString } from '../../shared/checklistUtils';
import type { SetupRuleInput } from '../setup/types';

import { buildCampaignRow, formatStringValue } from './campaignRow';
import type { CampaignChecklistRow } from './types';

type PolicyBibleCampaignRule = {
  chain: string;
  dateChangeAllowedDays?: number;
  geo: string;
  numberOfDateChanges?: string;
};

const POLICY_BIBLE_CAMPAIGN_RULES: Array<PolicyBibleCampaignRule> = [
  { chain: 'Accor', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Accor', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Ascott', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Ascott', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Ayana', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Clermont', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'EVT', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'EVT', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Grecotel', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Highgate', geo: 'Intl', dateChangeAllowedDays: 30, numberOfDateChanges: '1' },
  { chain: 'Hyatt', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Hyatt', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'IHG', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'IHG', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Kimpton', geo: 'Intl', dateChangeAllowedDays: 30, numberOfDateChanges: '1' },
  { chain: 'Langham', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Langham', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Marriott', geo: 'Intl/Aus', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Mulia', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Oberoi', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Oberoi', geo: 'India', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Pro Invest', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Pro Invest', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'India', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'International', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'USA', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard (GOLF wholesale)', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  {
    chain: 'Standard (GOLF WHOLESALE)',
    geo: 'Australia',
    dateChangeAllowedDays: 21,
    numberOfDateChanges: 'Unlimited',
  },
  { chain: 'TFE', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'TFE', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'The Star', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: '1' },
];

export function findPolicyBibleCampaignRule(setupInput?: SetupRuleInput) {
  const normalizedChainNames = getPolicyBibleChainCandidates(setupInput);
  const normalizedGeos = getPolicyBibleGeoCandidates(setupInput);

  if (normalizedChainNames.length === 0 || normalizedGeos.length === 0) {
    return undefined;
  }

  return POLICY_BIBLE_CAMPAIGN_RULES.find(
    (rule) =>
      normalizedChainNames.includes(normalizePolicyBibleKey(rule.chain) ?? '') &&
      normalizedGeos.some((normalizedGeo) => policyBibleGeoMatches(rule.geo, normalizedGeo)),
  );
}

export function buildPolicyBibleNumberRow({
  field,
  id,
  loadedValue,
  policyBibleRule,
  source,
  value,
}: Pick<CampaignChecklistRow, 'field' | 'id' | 'source' | 'value'> & {
  loadedValue?: number;
  policyBibleRule?: PolicyBibleCampaignRule;
}): CampaignChecklistRow {
  const expectedValue = policyBibleRule?.dateChangeAllowedDays;
  const approved = isFiniteNumber(loadedValue) && isFiniteNumber(expectedValue) && loadedValue === expectedValue;

  return buildCampaignRow({
    approved,
    dataPoints: [
      { label: 'Loaded value', value },
      { label: 'Expected value', value: formatOptionalNumber(expectedValue) },
    ],
    evidence: getPolicyBibleEvidence({ approved, expectedValue, loadedValue, policyBibleRule }),
    field,
    id,
    rule: 'Loaded value must match the Policy Bible.',
    source,
    value,
  });
}

export function buildPolicyBibleTextRow({
  field,
  id,
  loadedValue,
  policyBibleRule,
  source,
  value,
}: Pick<CampaignChecklistRow, 'field' | 'id' | 'source' | 'value'> & {
  loadedValue?: string | null;
  policyBibleRule?: PolicyBibleCampaignRule;
}): CampaignChecklistRow {
  const expectedValue = policyBibleRule?.numberOfDateChanges;
  const approved =
    !!normalizeString(loadedValue) &&
    !!normalizeString(expectedValue) &&
    normalizeComparable(loadedValue) === normalizeComparable(expectedValue);

  return buildCampaignRow({
    approved,
    dataPoints: [
      { label: 'Loaded value', value },
      { label: 'Expected value', value: formatStringValue(expectedValue) },
    ],
    evidence: getPolicyBibleEvidence({ approved, expectedValue, loadedValue, policyBibleRule }),
    field,
    id,
    rule: 'Loaded value must match the Policy Bible.',
    source,
    value,
  });
}

function policyBibleGeoMatches(ruleGeo: string, loadedGeo: string) {
  const normalizedRuleGeo = normalizePolicyBibleGeo(ruleGeo);

  if (!normalizedRuleGeo) {
    return false;
  }

  if (normalizedRuleGeo === loadedGeo) {
    return true;
  }

  return normalizedRuleGeo
    .split('/')
    .map((geoPart) => normalizePolicyBibleGeo(geoPart))
    .includes(loadedGeo);
}

function normalizePolicyBibleKey(value?: string | null) {
  return normalizeString(value)?.toLowerCase().replace(/&/g, 'and').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

function normalizePolicyBibleGeo(value?: string | null) {
  const normalizedValue = normalizePolicyBibleKey(value);

  switch (normalizedValue) {
    case 'au':
    case 'aus':
    case 'australia':
      return 'australia';
    case 'intl':
    case 'international':
      return 'intl';
    case 'intl/aus':
    case 'intl aus':
    case 'international/australia':
    case 'international australia':
      return 'intl/australia';
    case 'us':
    case 'usa':
    case 'united states':
    case 'united states of america':
      return 'usa';
    default:
      return normalizedValue;
  }
}

function getPolicyBibleChainCandidates(setupInput?: SetupRuleInput) {
  return uniqueNormalizedValues([
    setupInput?.chainName,
    ...extractPolicyBibleSegments(setupInput?.account?.contractTemplateId),
  ]);
}

function getPolicyBibleGeoCandidates(setupInput?: SetupRuleInput) {
  return uniqueNormalizedValues(
    [
      setupInput?.opportunity?.geo,
      setupInput?.property?.country,
      ...extractPolicyBibleSegments(setupInput?.account?.contractTemplateId),
    ],
    normalizePolicyBibleGeo,
  );
}

function extractPolicyBibleSegments(value?: string | null) {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    return [];
  }

  return normalizedValue
    .split(/(?:\s+-\s+|\s+\|\s+|:|,)/)
    .map((segment) => segment.replace(/\b(policy bible|contract template|le contracts?|geo)\b/gi, '').trim())
    .filter(Boolean);
}

function uniqueNormalizedValues(
  values: Array<string | null | undefined>,
  normalize: (value?: string | null) => string | undefined = normalizePolicyBibleKey,
) {
  return Array.from(new Set(values.map((value) => normalize(value)).filter((value): value is string => !!value)));
}

function getPolicyBibleEvidence({
  approved,
  expectedValue,
  loadedValue,
  policyBibleRule,
}: {
  approved: boolean;
  expectedValue?: number | string;
  loadedValue?: number | string | null;
  policyBibleRule?: PolicyBibleCampaignRule;
}) {
  if (!policyBibleRule) {
    return 'Policy Bible expected value is not available for the loaded chain and geo.';
  }

  if (expectedValue == null || expectedValue === '') {
    return 'Policy Bible expected value is blank or ambiguous for the loaded chain and geo.';
  }

  if (loadedValue == null || loadedValue === '') {
    return 'Loaded value is missing.';
  }

  return approved ? 'Loaded value matches the Policy Bible.' : 'Loaded value does not match the Policy Bible.';
}

function normalizeComparable(value?: string | null) {
  return value?.trim().toLowerCase() || '';
}
